//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React from 'react';
import CongratsIcon from '../../icons/CongratsIcon.svg';
import { useTranslation, Trans } from 'react-i18next';
import withZoom from '../Zoom/withZoom';
import { ProgressSpinner } from 'primereact/progressspinner';
import ProgressBar from './ProgressBar';
import settings from '../../Config/settings';
import RotatingFeatureBox from './RotatingFeatureBox';

interface LeftSectionProps {
  mode: string;
  step: number;
  steps: unknown[];
  images: string[];
  welcomeMessages: string[];
  descriptions: string[][];
  progress: number;
  showSuccess: boolean;
  isZoomedIn?: boolean;
  teamspaceCreationFailed: boolean;
  loopCreationFailed: boolean;
  taskProgressIndex: number;
  taskDescriptions: Record<string, string[]>;
  currentDescriptionLoop: string[];
  setCurrentDescriptionLoop: React.Dispatch<React.SetStateAction<string[]>>;
}

const LeftSection: React.FC<LeftSectionProps> = ({
  mode,
  step,
  steps,
  images,
  welcomeMessages,
  descriptions,
  progress,
  showSuccess,
  isZoomedIn,
  teamspaceCreationFailed,
  loopCreationFailed,
  taskProgressIndex,
  taskDescriptions,
  currentDescriptionLoop,
  setCurrentDescriptionLoop,
}) => {
  const { t } = useTranslation();
  // Always show the first step's content to keep left section consistent across steps
  const currentDescription = descriptions[0] || ['', ''];
  const isLastStep = step === steps.length;
  let currentTask = 'TEAMSPACE NAME';
  let shrinkImage = false;
  let shrinkImageLoop = false;
  if (isLastStep && !showSuccess && mode === 'loop' && !loopCreationFailed) {
    shrinkImageLoop = true;
  }

  let dynamicTaskDescription: string[] = ['', ''];
  if (
    isLastStep &&
    !showSuccess &&
    !teamspaceCreationFailed &&
    mode === 'teamspace'
  ) {
    currentTask = settings.TEAMSPACE_TASK_SEQUENCE[taskProgressIndex] ?? '';
    if (currentTask !== 'TEAMSPACE NAME') {
      shrinkImage = true;
    }
    dynamicTaskDescription = taskDescriptions[currentTask] || ['', ''];
  }

  const renderBottomSection = () => {
    if (mode === 'loop') {
      return (
        <div
          id="leftimg"
          data-testid="create-left-image-wrapper"
          className="relative mt-8 flex items-end justify-end"
        >
          <img
            id="loopimg"
            data-testid="loop-img"
            src={images[0]}
            alt="Loop Step Illustration"
            className="h-auto w-full max-w-md"
          />
        </div>
      );
    }
    return (
      <div
        id="leftimg"
        data-testid="create-left-image-wrapper"
        className="relative mt-8 flex items-end justify-end"
      >
        <img
          id="loopimg"
          data-testid="loop-img"
          src={images[0]}
          alt="Loop Step Illustration"
          className="max-h-[45vh] w-full max-w-md object-contain"
        />
      </div>
    );
  };

  return (
    <div id="leftsection" data-testid="left-section">
      <div id="lefttext" data-testid="left-text" className="mb-4">
        <p
          id="welcomemessage"
          data-testid="welcome-msg"
          className="mb-2 w-full text-2xl font-light text-[#4a4a56] dark:text-white"
        >
          {teamspaceCreationFailed
            ? t('retry-teamspace-creation')
            : welcomeMessages[0]}
        </p>
        <div
          id="description"
          data-testid="description"
          className="w-full text-sm text-[#666] dark:text-brand-disabled"
        >
          {teamspaceCreationFailed
            ? t('retry-teamspace-creation-description-1')
            : currentDescription[0]}
          {teamspaceCreationFailed ? (
            <>
              <br />
              <Trans
                i18nKey="retry-teamspace-creation-description-2"
                components={{ b: <b /> }}
                values={{
                  name: '<b>' + t('administrator') + '</b>',
                }}
              />
            </>
          ) : (
            currentDescription[1] && (
              <>
                <br />
                {currentDescription[1]}
              </>
            )
          )}
          {isLastStep &&
            !showSuccess &&
            !teamspaceCreationFailed &&
            mode === 'teamspace' &&
            currentTask !== 'TEAMSPACE NAME' && (
              <div
                id="id-create-left-task-section"
                data-testid="create-left-task-section"
              >
                <p className="align-middle text-lg font-medium leading-6 text-[#1D1D23]">
                  {' '}
                  {t('taskDescription')}
                </p>
                <div
                  id="task-description"
                  data-testid="task-description"
                  className="mt-5 flex items-center gap-3 rounded-lg border-2 border-[#E4E4F0] bg-white p-2.5 leading-relaxed shadow-sm"
                >
                  <div
                    id="id-create-left-spinner-wrapper"
                    data-testid="create-left-spinner-wrapper"
                    className="w-[50px] shrink"
                  >
                    <ProgressSpinner
                      style={{
                        width: '50px',
                        height: '50px',
                        ['--stroke' as string]: '#0000FF',
                      }}
                      strokeWidth="3"
                      fill="transparent"
                      animationDuration=".6s"
                    />
                  </div>
                  <div
                    id="id-create-left-task-details"
                    data-testid="create-left-task-details"
                  >
                    <p className="mb-2.5 align-middle text-base font-medium leading-[26px] text-[#1D1D23]">
                      {dynamicTaskDescription[0]}
                    </p>
                    <p className="align-middle text-sm font-normal leading-5 text-[#3F3F4A]">
                      {dynamicTaskDescription[1]}
                    </p>
                  </div>
                </div>
              </div>
            )}
          {isLastStep &&
            !showSuccess &&
            !loopCreationFailed &&
            mode === 'loop' && (
              <div
                id="id-create-left-loop-task-section"
                data-testid="create-left-loop-task-section"
              >
                <p className="align-middle text-lg font-medium leading-6 text-[#1D1D23]">
                  {' '}
                  {t('taskDescription')}
                </p>
                <div
                  id="task-description"
                  data-testid="task-description"
                  className="mt-5 flex items-center gap-3 rounded-lg border-2 border-[#E4E4F0] bg-white p-2.5 leading-relaxed shadow-sm"
                >
                  <div
                    id="id-create-left-loop-spinner-wrapper"
                    data-testid="create-left-loop-spinner-wrapper"
                    className="w-[50px] shrink"
                  >
                    <ProgressSpinner
                      style={{
                        width: '50px',
                        height: '50px',
                        ['--stroke' as string]: '#0000FF',
                      }}
                      strokeWidth="3"
                      fill="transparent"
                      animationDuration=".6s"
                    />
                  </div>
                  <div
                    id="id-create-left-loop-task-details"
                    data-testid="create-left-loop-task-details"
                  >
                    <p className="mb-2.5 align-middle text-base font-medium leading-[26px] text-[#1D1D23]">
                      {currentDescriptionLoop?.[0]
                        ? currentDescriptionLoop[0]
                        : t('starting-loop-title')}
                    </p>
                    <p className="align-middle text-sm font-normal leading-5 text-[#3F3F4A]">
                      {currentDescriptionLoop?.[1]
                        ? currentDescriptionLoop[1]
                        : t('starting-loop-desc')}
                    </p>
                  </div>
                </div>
              </div>
            )}
        </div>
      </div>
      {(mode === 'teamspace' ? step === 1 : !isLastStep) && (
        <div
          id="id-create-left-feature-wrapper"
          data-testid="create-left-feature-wrapper"
          className="my-4"
        >
          <RotatingFeatureBox />
        </div>
      )}
      {renderBottomSection()}
    </div>
  );
};

export default withZoom(LeftSection, 175);
